import { toast } from 'react-toastify';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import AppUpdater from '../Mobile/AppUpdater';

const PRODUCTION_API_URL = 'https://api.abdullahtraders.site/api';
const API_BASE_URL = (process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? PRODUCTION_API_URL : '/api')).trim().replace(/\/$/, '');
const MOBILE_API_BASE_URL = (process.env.REACT_APP_API_URL || PRODUCTION_API_URL).trim().replace(/\/$/, '');
const apiBase = () => Capacitor.isNativePlatform() ? MOBILE_API_BASE_URL : API_BASE_URL;
const TOKEN_KEY = 'abdullah_traders_token';
const TOKEN_REMEMBER_UNTIL_KEY = 'abdullah_traders_token_remember_until';
const REMEMBERED_LOGIN_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function tokenExpiresAt(token) {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return null;
    const normalizedPayload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(normalizedPayload));
    return Number.isFinite(payload.exp) ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function getStoredToken() {
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  if (sessionToken) {
    const expiresAt = tokenExpiresAt(sessionToken);
    if (!expiresAt || expiresAt > Date.now()) return sessionToken;
    sessionStorage.removeItem(TOKEN_KEY);
  }

  const persistentToken = localStorage.getItem(TOKEN_KEY);
  if (!persistentToken) return null;

  const tokenExpiry = tokenExpiresAt(persistentToken);
  const rememberUntil = Number(localStorage.getItem(TOKEN_REMEMBER_UNTIL_KEY));
  const expiresAt = rememberUntil ? Math.min(tokenExpiry || rememberUntil, rememberUntil) : tokenExpiry;
  if (!expiresAt || expiresAt <= Date.now()) {
    clearStoredToken();
    return null;
  }
  return persistentToken;
}

export function saveStoredToken(token, rememberMe = false) {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_REMEMBER_UNTIL_KEY);

  if (rememberMe) {
    const rememberUntil = Date.now() + REMEMBERED_LOGIN_DURATION_MS;
    const tokenExpiry = tokenExpiresAt(token);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_REMEMBER_UNTIL_KEY, String(Math.min(tokenExpiry || rememberUntil, rememberUntil)));
    return;
  }

  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_REMEMBER_UNTIL_KEY);
}

async function ensureInternetAvailable() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const error = new Error('You are offline. This request was not sent.');
    error.code = 'OFFLINE';
    throw error;
  }
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    const status = await AppUpdater.hasInternetConnection();
    if (!status.connected) {
      const error = new Error('No internet connection. This request was not sent.');
      error.code = 'OFFLINE';
      window.dispatchEvent(new Event('app:offline'));
      throw error;
    }
  } catch (error) {
    if (error.code === 'OFFLINE') throw error;
    error.code = 'OFFLINE';
    error.message = 'Internet status could not be verified. This request was not sent.';
    window.dispatchEvent(new Event('app:offline'));
    throw error;
  }
}

export async function apiRequest(path, { method = 'GET', body, token, auth = true, quiet = false } = {}) {
  await ensureInternetAvailable();
  const headers = {};
  const activeToken = token || (auth ? getStoredToken() : null);
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (activeToken) headers.Authorization = `Bearer ${activeToken}`;

  let status;
  let data;
  const url = `${apiBase()}${path.startsWith('/') ? path : `/${path}`}`;
  try {
    if (Capacitor.isNativePlatform()) {
      const response = await CapacitorHttp.request({
        url, method, headers,
        ...(body !== undefined ? { data: body } : {}),
        connectTimeout: 15000,
        readTimeout: 30000,
      });
      status = response.status;
      if (typeof response.data === 'string') {
        try { data = JSON.parse(response.data || '{}'); }
        catch { data = {}; }
      } else data = response.data || {};
    } else {
      const response = await fetch(url, {
        method, headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      status = response.status;
      data = await response.json().catch(() => ({}));
    }
  } catch (cause) {
    const message = `Could not connect to ${apiBase()}. Check your internet connection and try again.`;
    if (!quiet) toast.error(message);
    const error = new Error(message);
    error.cause = cause;
    throw error;
  }

  if (status === 401 && activeToken && auth) {
    window.dispatchEvent(new Event('auth:unauthorized'));
  }
  if (status < 200 || status >= 300 || data.success === false) {
    const message = data.message || `Request failed (${status}).`;
    if (!quiet) toast.error(message);
    const error = new Error(message);
    error.status = status;
    error.data = data;
    throw error;
  }
  return data;
}

export const MobileAppUpdatesAPI = {
  latest: async () => {
    const result = await apiRequest('/app-updates/latest', { auth: false, quiet: true });
    return { ...result, update: result.update ? {
      ...result.update,
      download_url: `${apiBase()}${result.update.download_url}`,
    } : null };
  },
  upload: async ({ file, versionCode, versionName, releaseNotes }) => {
    await ensureInternetAvailable();
    const token = getStoredToken();
    const response = await fetch(`${apiBase()}/app-updates/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/vnd.android.package-archive',
        'X-App-Version-Code': String(versionCode),
        'X-App-Version-Name': String(versionName),
        'X-Release-Notes': encodeURIComponent(releaseNotes || ''),
      },
      body: file,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) throw new Error(result.message || `Upload failed (${response.status}).`);
    return result;
  },
};

export const AuthAPI = {
  login: (credentials) => apiRequest('/auth/login', { method: 'POST', body: credentials, auth: false }),
  me: (token) => apiRequest('/auth/me', { token }),
};

export const BookerMobileAPI = {
  currentDay: () => apiRequest('/booker-mobile/days/current', { quiet: true }),
  startDay: (position) => apiRequest('/booker-mobile/days/start', { method: 'POST', body: position }),
  snapshot: (dayId) => apiRequest(`/booker-mobile/days/${dayId}/snapshot`),
  myCustomers: () => apiRequest('/booker-mobile/customers/mine'),
  updateMyCustomer: (id, customer) => apiRequest(`/booker-mobile/customers/mine/${id}`, { method: 'PATCH', body: customer }),
  endDay: (dayId, position) => apiRequest(`/booker-mobile/days/${dayId}/end`, { method: 'POST', body: position }),
  sync: (dayId, payload) => apiRequest(`/booker-mobile/days/${dayId}/sync`, { method: 'POST', body: payload }),
};

export const BookedOrdersAPI = {
  setup: () => apiRequest('/booker-mobile/setup'),
  configureWarehouse: (warehouse) => apiRequest('/booker-mobile/warehouse', { method: 'PUT', body: warehouse }),
  assign: (userId, bookerId) => apiRequest(`/booker-mobile/assignments/${userId}`, { method: 'PUT', body: { order_booker_id: bookerId } }),
  unassign: (userId) => apiRequest(`/booker-mobile/assignments/${userId}`, { method: 'DELETE' }),
  list: () => apiRequest('/booker-mobile/orders'),
  get: (id) => apiRequest(`/booker-mobile/orders/${id}`),
  update: (id, order) => apiRequest(`/booker-mobile/orders/${id}`, { method: 'PUT', body: order }),
  approve: (id) => apiRequest(`/booker-mobile/orders/${id}/approve`, { method: 'POST' }),
  reject: (id, reason) => apiRequest(`/booker-mobile/orders/${id}/reject`, { method: 'POST', body: { reason } }),
};

export const UsersAPI = {
  list: () => apiRequest('/auth/users'),
  create: (user) => apiRequest('/auth/users', { method: 'POST', body: user }),
  update: (id, user) => apiRequest(`/auth/users/${id}`, { method: 'PUT', body: user }),
  setActive: (id, isActive) => apiRequest(`/auth/users/${id}/active`, { method: 'PATCH', body: { is_active: isActive } }),
};

export const AuditAPI = {
  list: ({ limit = 50, offset = 0, search = '', entityType = '', outcome = '', startDate = '', endDate = '' } = {}) => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (search) params.set('search', search);
    if (entityType) params.set('entity_type', entityType);
    if (outcome) params.set('outcome', outcome);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    return apiRequest(`/auth/audit-logs?${params}`);
  },
};

export const DashboardAPI = {
  summary: () => apiRequest('/dashboard'),
};

export const CompanyAPI = {
  list: () => apiRequest('/companies'),
  create: (company) => apiRequest('/companies', { method: 'POST', body: company }),
  update: (id, company) => apiRequest(`/companies/${id}`, { method: 'PUT', body: company }),
};

export const OrderBookerAPI = {
  list: () => apiRequest('/order-bookers'),
  attendance: (startDate, endDate) => apiRequest(`/order-bookers/attendance?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`),
  create: (orderBooker) => apiRequest('/order-bookers', { method: 'POST', body: orderBooker }),
  update: (id, orderBooker) => apiRequest(`/order-bookers/${id}`, { method: 'PUT', body: orderBooker }),
};

export const CustomerAPI = {
  list: (options = {}) => {
    const params = new URLSearchParams();
    if (options.includeInactive) params.set('include_inactive', 'true');
    if (options.page) params.set('page', options.page);
    if (options.pageSize) params.set('page_size', options.pageSize);
    if (options.search) params.set('search', options.search);
    if (options.status && options.status !== 'all') params.set('status', options.status);
    if (options.orderBookerId && options.orderBookerId !== 'all') params.set('order_booker_id', options.orderBookerId);
    return apiRequest(`/customers${params.size ? `?${params}` : ''}`);
  },
  get: (id) => apiRequest(`/customers/${id}`),
  create: (customer) => apiRequest('/customers', { method: 'POST', body: customer }),
  update: (id, customer) => apiRequest(`/customers/${id}`, { method: 'PUT', body: customer }),
  remove: (id) => apiRequest(`/customers/${id}`, { method: 'DELETE' }),
  setActive: (id, isActive) => apiRequest(`/customers/${id}/active`, { method: 'PATCH', body: { is_active: isActive } }),
};

export const SalesAPI = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    const query = params.toString();
    return apiRequest(`/sales${query ? `?${query}` : ''}`);
  },
  printRange: (filters = {}, page = 1) => {
    const params = new URLSearchParams({ page: String(page) });
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    return apiRequest(`/sales/print?${params}`);
  },
  get: (id) => apiRequest(`/sales/${id}`),
  create: (sale) => apiRequest('/sales', { method: 'POST', body: sale }),
  update: (id, sale) => apiRequest(`/sales/${id}`, { method: 'PUT', body: sale }),
};

export const ReturnsAPI = {
  listForSale: (saleId) => apiRequest(`/returns/sale/${saleId}`),
  createForSale: (saleId, saleReturn) => apiRequest(`/returns/sale/${saleId}`, { method: 'POST', body: saleReturn }),
  get: (id) => apiRequest(`/returns/${id}`),
};

export const PaymentsAPI = {
  overview: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value !== '' && value != null) params.set(key, value); });
    const query = params.toString();
    return apiRequest(`/payments/overview${query ? `?${query}` : ''}`);
  },
  statementForCustomer: (customerId) => apiRequest(`/payments/customers/${customerId}`),
  createForSale: (saleId, payment) => apiRequest(`/payments/sales/${saleId}`, { method: 'POST', body: payment }),
};

export const ExpensesAPI = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value !== '' && value != null) params.set(key, value); });
    const query = params.toString();
    return apiRequest(`/expenses${query ? `?${query}` : ''}`);
  },
  create: (expense) => apiRequest('/expenses', { method: 'POST', body: expense }),
  remove: (id) => apiRequest(`/expenses/${id}`, { method: 'DELETE' }),
};

export const ReportsAPI = {
  stock: (companyId = '') => apiRequest(`/reports/stock${companyId ? `?company_id=${encodeURIComponent(companyId)}` : ''}`),
  customerCredit: () => apiRequest('/reports/customer-credit'),
  orderBookerCredit: () => apiRequest('/reports/order-booker-credit'),
  productSales: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    const query = params.toString();
    return apiRequest(`/reports/product-sales${query ? `?${query}` : ''}`);
  },
  orderBookerSales: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    const query = params.toString();
    return apiRequest(`/reports/order-booker-sales${query ? `?${query}` : ''}`);
  },
  orderBookerDetail: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    const query = params.toString();
    return apiRequest(`/reports/order-booker-detail${query ? `?${query}` : ''}`);
  },
  profit: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    const query = params.toString();
    return apiRequest(`/reports/profit${query ? `?${query}` : ''}`);
  },
  profitByInvoice: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    const query = params.toString();
    return apiRequest(`/reports/profit-by-invoice${query ? `?${query}` : ''}`);
  },
};

export const ProductAPI = {
  list: (options = {}) => {
    const params = new URLSearchParams();
    if (options.includeInactive) params.set('include_inactive', 'true');
    if (options.page) params.set('page', options.page);
    if (options.pageSize) params.set('page_size', options.pageSize);
    if (options.search) params.set('search', options.search);
    if (options.status && options.status !== 'all') params.set('status', options.status);
    if (options.companyId && options.companyId !== 'all') params.set('company_id', options.companyId);
    if (options.inStock) params.set('in_stock', 'true');
    return apiRequest(`/products${params.size ? `?${params}` : ''}`);
  },
  get: (id) => apiRequest(`/products/${id}`),
  create: (product) => apiRequest('/products', { method: 'POST', body: product }),
  update: (id, product) => apiRequest(`/products/${id}`, { method: 'PUT', body: product }),
  remove: (id) => apiRequest(`/products/${id}`, { method: 'DELETE' }),
  setActive: (id, isActive) => apiRequest(`/products/${id}/active`, { method: 'PATCH', body: { is_active: isActive } }),
};

export const InventoryAPI = {
  movements: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) params.set(key, value);
    });
    const query = params.toString();
    return apiRequest(`/inventory${query ? `?${query}` : ''}`);
  },
  createMovement: (movement) => apiRequest('/inventory/movements', { method: 'POST', body: movement }),
};

export { API_BASE_URL, MOBILE_API_BASE_URL, TOKEN_KEY };
